/**
 * The paperwork on one driver or one car.
 *
 * One component for both owners, because the difference between them is the list of kinds
 * and nothing else. Two copies would drift, and the half that drifted would be the vehicle
 * one — which is the half that has never had a screen at all, despite `ShiftRules` refusing
 * on it since Phase 3.
 *
 * A dialog rather than a row that expands, because recording a document is a small form and
 * the list it belongs to is already four columns wide. The dialog stays open after a
 * document is recorded: a new driver arrives with a licence, a certificate and a medical in
 * the same envelope, and closing after each one would mean finding his row again twice.
 *
 * Expiry is the whole point of this table, so the dates carry their consequence rather than
 * being rendered as dates. A licence that lapsed is why somebody could not sign on this
 * morning; a licence lapsing in three weeks is a job for this week.
 */

import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui';
import {
  driverDocumentKinds,
  requiredKinds,
  vehicleDocumentKinds,
  type DocumentKind,
  type DocumentOwner,
  type FleetDocument,
  type NewDocument,
} from './api';

interface Props {
  ownerType: DocumentOwner;
  ownerId: string;

  /** Whose paperwork this is, so the dialog says it in its own title. */
  ownerName: string;
  documents: FleetDocument[];
  busy: boolean;

  /** The page's last failure, repeated here because the dialog covers the page's banner. */
  error: string | null;
  onRecord: (document: NewDocument) => Promise<boolean>;
  onRemove: (documentId: string) => void;
  onClose: () => void;
}

export function DocumentEditor({
  ownerType,
  ownerId,
  ownerName,
  documents,
  busy,
  error,
  onRecord,
  onRemove,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const form = useId();

  const kinds = ownerType === 'Driver' ? driverDocumentKinds : vehicleDocumentKinds;

  const [kind, setKind] = useState<DocumentKind>(kinds[0]);
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');

  const required = requiredKinds[ownerType];
  const holdsRequired = documents.some((d) => d.kind === required && !d.isExpired);

  async function submit(event: FormEvent) {
    event.preventDefault();

    const recorded = await onRecord({
      ownerType,
      ownerId,
      kind,
      description: description.trim() === '' ? null : description.trim(),
      number: reference.trim() === '' ? null : reference.trim(),
      issuedOn: issuedOn === '' ? null : issuedOn,
      expiresOn: expiresOn === '' ? null : expiresOn,
    });

    // Cleared once it is on the record, never before. A rejected save used to take the
    // number and both dates with it, leaving somebody to read them off the paper again.
    if (!recorded) return;

    setDescription('');
    setReference('');
    setIssuedOn('');
    setExpiresOn('');
  }

  return (
    <Dialog
      open
      wide
      title={`${t('fleet.documents.title')} · ${ownerName}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('fleet.close')}
          </Button>
          <Button variant="primary" type="submit" form={form} disabled={busy}>
            {t('fleet.documents.record')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== null && <Banner tone="danger">{error}</Banner>}

        {!holdsRequired && (
          // Said here rather than left for a driver to discover at six in the morning. This
          // is the exact condition ShiftRules refuses on, named on the screen that can fix it.
          <Banner tone="warn">
            {t('fleet.documents.blocking', { kind: t(`fleet.documentKinds.${required}`) })}
          </Banner>
        )}

        {documents.length === 0 ? (
          <EmptyState>{t('fleet.documents.none')}</EmptyState>
        ) : (
          <Table>
            <thead>
              <Tr>
                <Th>{t('fleet.documents.kind')}</Th>
                <Th>{t('fleet.documents.number')}</Th>
                <Th>{t('fleet.columns.expires')}</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {documents.map((document) => (
                <Tr key={document.id}>
                  <Td wrap>
                    {document.kind === 'Other' && document.description !== null
                      ? document.description
                      : t(`fleet.documentKinds.${document.kind}`)}
                  </Td>
                  <Td>{document.number ?? '—'}</Td>
                  <Td>
                    {/* The date as the API sends it, `yyyy-MM-dd`, exactly as the panel had
                        it. The tenant's short day format carries no year, and this is the
                        one table where the year is the fact being read: a licence valid to
                        March 2030 and one valid to March 2026 must not read alike. */}
                    {document.expiresOn === null ? (
                      t('fleet.documents.noExpiry')
                    ) : document.isExpired ? (
                      <Pill tone="danger" soft>
                        {t('fleet.documents.expired', { date: document.expiresOn })}
                      </Pill>
                    ) : document.expiresSoon ? (
                      <Pill tone="warn" soft>
                        {t('fleet.documents.expiringSoon', { date: document.expiresOn })}
                      </Pill>
                    ) : (
                      t('fleet.documents.valid', { date: document.expiresOn })
                    )}
                  </Td>
                  <Td>
                    <span className="flex justify-end">
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={busy}
                        onClick={() => onRemove(document.id)}
                      >
                        {t('fleet.remove')}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}

        <form id={form} onSubmit={(event) => void submit(event)} className="flex flex-col gap-3">
          <Field id="kind" label={t('fleet.documents.kind')}>
            <Select
              value={kind}
              onChange={(event) => setKind(event.target.value as DocumentKind)}
            >
              {kinds.map((option) => (
                <option key={option} value={option}>
                  {t(`fleet.documentKinds.${option}`)}
                </option>
              ))}
            </Select>
          </Field>

          {kind === 'Other' && (
            <Field
              id="description"
              label={t('fleet.documents.description')}
              help={t('fleet.documents.descriptionHelp')}
            >
              <Input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                required
              />
            </Field>
          )}

          <Field id="number" label={t('fleet.documents.number')}>
            <Input value={reference} onChange={(event) => setReference(event.target.value)} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field id="issuedOn" label={t('fleet.documents.issuedOn')}>
              <Input
                type="date"
                value={issuedOn}
                onChange={(event) => setIssuedOn(event.target.value)}
              />
            </Field>

            <Field id="expiresOn" label={t('fleet.documents.expiresOn')} help={t('fleet.documents.datesHelp')}>
              <Input
                type="date"
                value={expiresOn}
                onChange={(event) => setExpiresOn(event.target.value)}
              />
            </Field>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
